/**
 * Avisos (toasts), con acción «Deshacer».
 *
 * POR QUÉ «DESHACER» Y NO UN DIÁLOGO DE CONFIRMACIÓN
 *
 * Un «¿seguro?» por cada borrado entrena al usuario a pulsar «sí» sin leer, así que deja de
 * proteger a la tercera vez. Un deshacer de 8 segundos protege de verdad y no interrumpe.
 *
 * La confirmación fuerte se reserva para lo que duele y no se puede deshacer: borrar un
 * inmueble y rescindir un contrato.
 *
 * El estado vive fuera de React (una lista de módulo + suscriptores) para poder lanzar un
 * aviso desde un `onSuccess` de TanStack Query sin arrastrar un contexto hasta allí.
 */
import { useEffect, useState } from 'react';
import { Icon, type IconName } from '../Icon';
import styles from './Toaster.module.css';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  /** Acción secundaria, normalmente «Deshacer». */
  action?: { label: string; run: () => void | Promise<void> };
  durationMs: number;
}

let nextId = 1;
let toasts: Toast[] = [];
const listeners = new Set<(items: Toast[]) => void>();

function emit(): void {
  for (const listener of listeners) listener(toasts);
}

export function dismissToast(id: number): void {
  toasts = toasts.filter((toast) => toast.id !== id);
  emit();
}

export function showToast(
  message: string,
  options: { tone?: ToastTone; action?: Toast['action']; durationMs?: number } = {},
): number {
  const id = nextId++;
  const toast: Toast = {
    id,
    message,
    tone: options.tone ?? 'info',
    ...(options.action ? { action: options.action } : {}),
    /**
     * 8 s cuando hay acción y 4 s cuando no. Con menos, «Deshacer» no da tiempo a leer el
     * mensaje, entenderlo y decidir; con más, la pila de avisos tapa la pantalla.
     */
    durationMs: options.durationMs ?? (options.action ? 8_000 : 4_000),
  };
  toasts = [...toasts, toast];
  emit();
  return id;
}

export const toast = {
  info: (message: string, action?: Toast['action']) => showToast(message, { tone: 'info', ...(action ? { action } : {}) }),
  success: (message: string, action?: Toast['action']) =>
    showToast(message, { tone: 'success', ...(action ? { action } : {}) }),
  warning: (message: string) => showToast(message, { tone: 'warning' }),
  error: (message: string) => showToast(message, { tone: 'danger', durationMs: 7_000 }),
};

const TONE_ICON: Record<ToastTone, IconName> = {
  info: 'info',
  success: 'info',
  warning: 'bell',
  danger: 'info',
};

function ToastItem({ item }: { item: Toast }) {
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => dismissToast(item.id), item.durationMs);
    return () => clearTimeout(timer);
  }, [item.id, item.durationMs]);

  return (
    <div className={[styles.toast, styles[item.tone]].join(' ')} role="status">
      <span className={styles.icon} aria-hidden="true">
        <Icon name={TONE_ICON[item.tone]} size={15} />
      </span>
      <span className={styles.message}>{item.message}</span>

      {item.action && (
        <button
          type="button"
          className={styles.action}
          disabled={running}
          onClick={async () => {
            setRunning(true);
            try {
              await item.action!.run();
            } finally {
              dismissToast(item.id);
            }
          }}
        >
          {running ? '…' : item.action.label}
        </button>
      )}

      <button
        type="button"
        className={styles.close}
        aria-label="Cerrar aviso"
        onClick={() => dismissToast(item.id)}
      >
        ×
      </button>
    </div>
  );
}

export function ToastViewport() {
  const [items, setItems] = useState<Toast[]>(toasts);

  useEffect(() => {
    listeners.add(setItems);
    return () => {
      listeners.delete(setItems);
    };
  }, []);

  if (items.length === 0) return null;

  return (
    /**
     * `aria-live="polite"` y no `assertive`: un aviso de «guardado» no debe interrumpir lo que
     * el lector de pantalla esté leyendo. Los errores de formulario, que sí urgen, se anuncian
     * en el propio campo con `role="alert"`.
     */
    <div className={styles.viewport} aria-live="polite" aria-atomic="false">
      {items.map((item) => (
        <ToastItem key={item.id} item={item} />
      ))}
    </div>
  );
}
